import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Filter, LayoutGrid, List, Search, X } from '../../icons';
import { TASK_PRIORITIES, TASK_PRIORITY_META } from '../../api/tasks';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import {
  activeFilterCount, EMPTY_TASK_FILTER,
  type TaskFilterState, type TaskViewMode,
} from './task-filter';
import './TaskBoardShell.css';

interface TaskToolbarProps {
  view: TaskViewMode;
  onViewChange: (v: TaskViewMode) => void;
  filter: TaskFilterState;
  onFilterChange: (f: TaskFilterState) => void;
  /** 필터·검색 적용 후 건수 — 무언가 걸려 있을 때만 표시한다. */
  resultCount: number;
}

/**
 * TaskToolbar (2026-08-18 시안 ②) — 보드 상단 줄:
 * 좌측 [목록보기 | 칸반보기] 세그먼트, 우측 [필터] · [업무 검색].
 *
 * 신규 파일 · additive — 헤더(제목/배지/＋)와 칸반은 건드리지 않고 그 사이에 한 줄 추가한다.
 * 필터는 팝오버(담당·우선순위·마감) 한 장이며, 판정 로직은 `task-filter.ts` 가 갖는다.
 */
export function TaskToolbar({ view, onViewChange, filter, onFilterChange, resultCount }: TaskToolbarProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const n = activeFilterCount(filter);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function patch(next: Partial<TaskFilterState>) {
    onFilterChange({ ...filter, ...next });
  }

  const showCount = n > 0 || filter.query.trim() !== '';

  return (
    <div className="tb-toolbar">
      <div className="tb-views" role="tablist" aria-label={tTask('task.toolbar.view_aria')}>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'list'}
          className={`tb-view${view === 'list' ? ' tb-view--on' : ''}`}
          onClick={() => onViewChange('list')}
        >
          <List size={13} aria-hidden />
          {tTask('task.toolbar.view_list')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'kanban'}
          className={`tb-view${view === 'kanban' ? ' tb-view--on' : ''}`}
          onClick={() => onViewChange('kanban')}
        >
          <LayoutGrid size={13} aria-hidden />
          {tTask('task.toolbar.view_kanban')}
        </button>
      </div>

      {showCount && (
        <span className="tb-result" role="status">{tTask('task.toolbar.result', { n: String(resultCount) })}</span>
      )}

      <div className="tb-tools">
        <div className="tb-filter-wrap" ref={popRef}>
          <button
            type="button"
            className={`tb-btn${n > 0 || open ? ' tb-btn--on' : ''}`}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-label={tTask('task.toolbar.filter_aria')}
            onClick={() => setOpen((v) => !v)}
          >
            <Filter size={13} aria-hidden />
            {tTask('task.toolbar.filter')}
            {n > 0 && <span className="tb-btn-badge">{n}</span>}
          </button>

          {open && (
            <div className="tb-pop" role="dialog" aria-label={tTask('task.toolbar.filter')}>
              <div className="tb-pop-row">
                <span className="tb-pop-lb">{tTask('task.toolbar.f_scope')}</span>
                <div className="tb-chips">
                  <button
                    type="button"
                    className={`tb-chip${filter.scope === 'all' ? ' tb-chip--on' : ''}`}
                    aria-pressed={filter.scope === 'all'}
                    onClick={() => patch({ scope: 'all' })}
                  >{tTask('task.toolbar.f_scope_all')}</button>
                  <button
                    type="button"
                    className={`tb-chip${filter.scope === 'mine' ? ' tb-chip--on' : ''}`}
                    aria-pressed={filter.scope === 'mine'}
                    onClick={() => patch({ scope: 'mine' })}
                  >{tTask('task.toolbar.f_scope_mine')}</button>
                </div>
              </div>

              <div className="tb-pop-row">
                <span className="tb-pop-lb">{tTask('task.toolbar.f_priority')}</span>
                <div className="tb-chips">
                  <button
                    type="button"
                    className={`tb-chip${filter.priority === 'ALL' ? ' tb-chip--on' : ''}`}
                    aria-pressed={filter.priority === 'ALL'}
                    onClick={() => patch({ priority: 'ALL' })}
                  >{tTask('task.toolbar.f_all')}</button>
                  {TASK_PRIORITIES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`tb-chip${filter.priority === p ? ' tb-chip--on' : ''}`}
                      aria-pressed={filter.priority === p}
                      style={filter.priority === p
                        ? { background: TASK_PRIORITY_META[p].color, borderColor: 'transparent', color: '#fff' }
                        : undefined}
                      onClick={() => patch({ priority: p })}
                    >{TASK_PRIORITY_META[p].label}</button>
                  ))}
                </div>
              </div>

              <div className="tb-pop-row">
                <span className="tb-pop-lb">{tTask('task.toolbar.f_due')}</span>
                <div className="tb-chips">
                  {([
                    ['all', tTask('task.toolbar.f_all')],
                    ['overdue', tTask('task.toolbar.f_due_overdue')],
                    ['today', tTask('task.toolbar.f_due_today')],
                    ['week', tTask('task.toolbar.f_due_week')],
                  ] as const).map(([v, lb]) => (
                    <button
                      key={v}
                      type="button"
                      className={`tb-chip${filter.due === v ? ' tb-chip--on' : ''}`}
                      aria-pressed={filter.due === v}
                      onClick={() => patch({ due: v })}
                    >{lb}</button>
                  ))}
                </div>
              </div>

              <div className="tb-pop-foot">
                <button
                  type="button"
                  className="tb-reset"
                  disabled={n === 0}
                  onClick={() => onFilterChange({ ...EMPTY_TASK_FILTER, query: filter.query })}
                >{tTask('task.toolbar.reset')}</button>
              </div>
            </div>
          )}
        </div>

        <div className="tb-search">
          <Search size={13} aria-hidden />
          <input
            type="search"
            className="tb-search-input"
            placeholder={tTask('task.toolbar.search_placeholder')}
            aria-label={tTask('task.toolbar.search_aria')}
            value={filter.query}
            onChange={(e) => patch({ query: e.target.value })}
          />
          {filter.query !== '' && (
            <button
              type="button"
              className="tb-search-clear"
              aria-label={tTask('task.btn.close')} title={tTask('task.btn.close')}
              onClick={() => patch({ query: '' })}
            >
              <X size={12} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
